import apiClient from './apiClient';

// ============================================================
// Types — matching backend Go models (FN210 master + FN211 detail)
// ============================================================

export interface FN210 {
  fn210_01: string;          // 買收單號 (PK)
  fn210_10: string;          // 出庫通知單號
  fn210_11: string | null;   // 交運日期
  fn210_12: string;          // 廠商代號
  fn210_13: string;          // 幣別
  fn210_14: number;          // 匯率
  fn210_15: string;          // 輸入人員
  fn210_16: string;          // 部門代號
  fn210_17: string;          // 備註
  fn210_18: number;          // 總交運量
  fn210_19: number;          // 憑證類別(已停用)
  fn210_20: number;          // 原幣交運金額
  fn210_201: number;         // 原幣交運總額
  fn210_21: number;          // 原幣稅額
  fn210_22: number;          // 本幣交運金額
  fn210_23: string;          // 出庫庫位
  fn210_24: number;          // 明細行數
  fn210_25: boolean;         // 計稅旗標
  fn210_251: number;         // 發票種類
  fn210_26: string;          // 發票號碼
  fn210_27: string | null;   // 發票日期
  fn210_28: string;          // 客訴工單號
  fn210_36: string;          // 關聯單號
  fn210_37: string | null;   // 建立日期
  fn210_38: string | null;   // 異動日期
  fn210_39: string;          // 異動人員
  fn210_43: boolean;         // 旗標
  fn210_80: string;          // 核准狀態
  fn210_91: string;          // 建立人員
  fn210_92: string | null;   // 建立日期
  fn210_93: string;          // 異動人員

  details?: FN211[];
}

export interface FN211 {
  fn211_01: string;          // 買收單號 (FK)
  fn211_02: number;          // 項次
  fn211_11: string;          // 產品代號
  fn211_12: string;          // 批號
  fn211_13: string;          // 色號
  fn211_14: number;          // 交運量
  fn211_15: string;          // 單位
  fn211_16: number;          // 單價
  fn211_161: number;         // 單價(副)
  fn211_17: string;          // 備註1(色號中文名)
  fn211_18: string;          // 備註2(色號英文名)
  fn211_19: number;          // 金額
  fn211_20: number;          // 成本金額
  fn211_91: string;          // 建立人員
  fn211_92: string | null;   // 建立日期
  fn211_93: string;          // 異動人員
}

// ============================================================
// Request / Response types
// ============================================================

export interface N211ListResponse {
  data: FN210[];
  total: number;
  page: number;
  size: number;
}

export interface N211CreateRequest {
  master: Partial<FN210>;
  details: Partial<FN211>[];
}

export interface N211SearchResponse {
  data: FN210[];
  total: number;
  page: number;
  size: number;
}

export interface ValidationError {
  field: string;
  rule: string;
  message: string;
}

// ============================================================
// API methods
// ============================================================

export const n211Api = {
  async list(page = 1, size = 20): Promise<N211ListResponse> {
    const res = await apiClient.get('/api/n211', { params: { page, size } });
    return res.data;
  },

  async get(id: string): Promise<FN210> {
    const res = await apiClient.get(`/api/n211/${encodeURIComponent(id)}`);
    return res.data;
  },

  async search(keyword: string, page = 1, size = 20, dateFrom?: string, dateTo?: string): Promise<N211SearchResponse> {
    const params: Record<string, string | number> = { keyword, page, size };
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    const res = await apiClient.get('/api/n211/search', { params });
    return res.data;
  },

  async create(data: N211CreateRequest): Promise<FN210> {
    const res = await apiClient.post('/api/n211', data);
    return res.data;
  },

  async update(id: string, data: N211CreateRequest): Promise<FN210> {
    const res = await apiClient.put(`/api/n211/${encodeURIComponent(id)}`, data);
    return res.data;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/api/n211/${encodeURIComponent(id)}`);
  },

  async lookupName(type: string, code: string): Promise<string> {
    if (!code || !code.trim()) return '';
    try {
      const res = await apiClient.get(
        `/api/n211/lookup/${encodeURIComponent(type)}/${encodeURIComponent(code.trim())}`
      );
      return res.data.name || '';
    } catch {
      return '';
    }
  },
};
